<template>
  <div class="video-container" :style="containerStyle" @click="handleVideoClick">
    <video ref="videoBg" class="video-bg" autoplay :controls="false" playsinline
      webkit-playsinline x5-playsinline muted disablePictureInPicture controlslist="nodownload noplaybackrate">
    </video>
    <video ref="video" class="video-player" :style="{ objectFit: fit }" autoplay :controls="false" playsinline
      webkit-playsinline x5-playsinline muted disablePictureInPicture controlslist="nodownload noplaybackrate"
      @loadedmetadata="handleVideoReady" @loadeddata="handleVideoReady" @canplay="handleVideoReady"
      @playing="handleVideoReady">
    </video>
    <div v-if="showVideoMask" class="video-mask">
      <i class="iconfont icon-shipinjiankong"></i>
      <span>{{ $t(videoMaskText) }}</span>
    </div>
    <div v-if="videoStatus === 'ready'" class="video-crosshair">
      <span class="video-crosshair__line video-crosshair__line--x"></span>
      <span class="video-crosshair__line video-crosshair__line--y"></span>
      <span class="video-crosshair__center"></span>
    </div>
    <div v-if="isVideoStalled && !showVideoMask" class="video-stall-tip">{{ $t("视频可能卡顿") }}</div>
  </div>
</template>

<script>
import { speakText } from '@/utils/index'

const STATS_INTERVAL_MS = 1000
const FIRST_FRAME_TIMEOUT_MS = 5000
const MEDIA_STALL_TIMEOUT_MS = 4000
const DISCONNECTED_GRACE_MS = 1000
const CONNECT_TIMEOUT_MS = 10000
const FAST_RECONNECT_DELAYS_MS = [500, 1000, 2000]
const SLOW_RECONNECT_DELAY_MS = 5000

export default {
  name: 'VideoBox',
  props: {
    fit: {
      type: String,
      default: 'contain',
      validator: v => ['contain', 'cover', 'fill', 'none', 'scale-down'].includes(v)
    },
    width: {
      type: [String, Number],
      default: '100%'
    },
    height: {
      type: [String, Number],
      default: '100%'
    },
    plane: {
      type: Object,
      deep: true
    },
    flyToSinglePlane: {
      type: Function
    },
    publishFun: {
      type: Function
    }
  },
  data () {
    return {
      peerConnection: null,
      whepResource: null,
      playRequestId: 0,
      enableGuided: false, // 是否发送点飞 测试代码
      videoStatus: 'connecting',
      videoCheckTimer: null,
      videoCheckCount: 0,
      stallCheckTimer: null,
      statsTimer: null,
      connectionTimer: null,
      reconnectTimer: null,
      reconnectAttempt: 0,
      manualStop: false,
      videoSuspended: false,
      handleVideoPageHideBound: null,
      handleVideoPageShowBound: null,
      handleVideoVisibilityBound: null,
      lastVideoTime: 0,
      stallCount: 0,
      isVideoStalled: false
    }
  },
  computed: {
    // 视频点飞水平视场角由飞机资料接口提供，不再使用终端本机缓存。
    guidedFov () {
      const value = Number(this.plane && this.plane.camera_fov)
      return Number.isFinite(value) && value >= 20 && value <= 120 ? value : 55
    },
    playUrl () {
      const planeCode = String(this.plane?.plane_code ?? '').trim()
      if (!/^\d{6}$/.test(planeCode)) return ''
      return `https://flicube.site/srs6/rtc/v1/whep/?app=live&stream=FC-${planeCode}&codec=hevc`
    },
    containerStyle () {
      const toCssSize = v => (typeof v === 'number' ? `${v}px` : v)
      return {
        width: toCssSize(this.width),
        height: toCssSize(this.height)
      }
    },
    // 飞机航向
    heading () {
      if (this.plane && this.plane.planeState) {
        return this.plane.planeState.heading
      }
      return null
    },
    // 飞机经纬高
    position () {
      const position = this.plane && this.plane.planeState ? this.plane.planeState.position : null
      return Array.isArray(position) ? position : []
    },
    // 是否处于下降阶段，只有此时允许视频点飞微调
    isDescentActive () {
      const value = this.plane && this.plane.planeState ? this.plane.planeState.isDescentActive : null
      const isDescending = value === true || value === 1 || value === '1'
      const goodsDownValue = this.plane && this.plane.planeState ? this.plane.planeState.isAutoGoodsDown : null
      const isAutoGoodsDown = goodsDownValue === true || goodsDownValue === 1 || goodsDownValue === '1'
      return isDescending && !isAutoGoodsDown
    },
    laserHeightValue () {
      const value = this.plane && this.plane.planeState ? Number(this.plane.planeState.laserAlt) : 0
      return Number.isFinite(value) ? value : 0
    },
    hasHealthyLaserHeight () {
      return this.laserHeightValue > 0
    },
    // 视频点飞使用的激光高度：无有效激光高度时按 1.5m 兜底，降低误点位移
    effectiveLaserHeight () {
      return this.hasHealthyLaserHeight ? this.laserHeightValue : 1.5
    },
    showVideoMask () {
      return this.videoStatus !== 'ready'
    },
    videoMaskText () {
      if (this.videoStatus === 'error') {
        return '视频连接失败'
      }
      if (this.videoStatus === 'noFrame') {
        return '暂无视频画面'
      }
      if (this.videoStatus === 'reconnecting') {
        return '视频重新连接中'
      }
      return '视频连接中'
    }
  },
  mounted () {
    this.handleVideoPageHideBound = () => this.suspendVideo()
    this.handleVideoPageShowBound = () => this.resumeVideo()
    this.handleVideoVisibilityBound = () => {
      if (document.hidden) this.suspendVideo()
      else this.resumeVideo()
    }
    window.addEventListener('pagehide', this.handleVideoPageHideBound)
    window.addEventListener('pageshow', this.handleVideoPageShowBound)
    document.addEventListener('visibilitychange', this.handleVideoVisibilityBound)
    this.startPlay()
  },
  beforeDestroy () {
    this.manualStop = true
    if (this.enableGuided) {
      this.$emit('guided-change', false)
    }
    this.clearVideoCheckTimer()
    this.clearStallCheckTimer()
    this.clearConnectionTimer()
    this.clearStatsTimer()
    this.clearReconnectTimer()
    this.playRequestId += 1
    this.closeVideoSession()
    window.removeEventListener('pagehide', this.handleVideoPageHideBound)
    window.removeEventListener('pageshow', this.handleVideoPageShowBound)
    document.removeEventListener('visibilitychange', this.handleVideoVisibilityBound)
  },
  methods: {
    async startPlay (automatic = false) {
      if (!automatic) {
        this.manualStop = false
        this.reconnectAttempt = 0
        this.clearReconnectTimer()
      }
      const requestId = ++this.playRequestId
      this.clearVideoCheckTimer()
      this.clearStallCheckTimer()
      this.clearConnectionTimer()
      this.clearStatsTimer()
      this.isVideoStalled = false
      this.videoStatus = automatic ? 'reconnecting' : 'connecting'
      this.closeVideoSession()

      if (this.manualStop || !this.playUrl) {
        this.videoStatus = 'noFrame'
        return
      }

      try {
        const capabilities = window.RTCRtpReceiver?.getCapabilities?.('video')
        const h265Codecs = capabilities?.codecs?.filter(codec => /video\/(h265|hevc)/i.test(codec.mimeType)) || []
        if (!h265Codecs.length) {
          throw new Error('当前浏览器未公布 WebRTC H.265 解码能力')
        }

        const peer = new RTCPeerConnection()
        this.peerConnection = peer
        this.connectionTimer = setTimeout(() => {
          if (requestId === this.playRequestId && this.peerConnection === peer) {
            this.scheduleReconnect('WebRTC 连接超时')
          }
        }, CONNECT_TIMEOUT_MS)
        const transceiver = peer.addTransceiver('video', { direction: 'recvonly' })
        transceiver.setCodecPreferences(h265Codecs)
        if ('jitterBufferTarget' in transceiver.receiver) {
          try { transceiver.receiver.jitterBufferTarget = 50 } catch (error) { console.warn('设置 WebRTC 目标抖动缓冲失败', error) }
        }
        peer.ontrack = event => {
          if (requestId !== this.playRequestId || this.peerConnection !== peer) return
          const stream = event.streams[0] || new MediaStream([event.track])
          this.$refs.video.srcObject = stream
          if (this.$refs.videoBg) this.$refs.videoBg.srcObject = stream
          this.$refs.video.play().catch(() => {})
        }
        peer.onconnectionstatechange = () => {
          if (requestId !== this.playRequestId || this.peerConnection !== peer) return
          if (peer.connectionState === 'connected') {
            this.clearConnectionTimer()
            this.clearReconnectTimer()
            this.startWebRtcStatsCheck(peer, requestId)
          } else if (peer.connectionState === 'failed') {
            this.scheduleReconnect('WebRTC 连接失败', 0)
          } else if (peer.connectionState === 'disconnected') {
            this.scheduleReconnect('WebRTC 连接断开', DISCONNECTED_GRACE_MS)
          }
        }

        await peer.setLocalDescription(await peer.createOffer())
        await this.waitForIceGathering(peer)
        if (requestId !== this.playRequestId || this.peerConnection !== peer) return
        const requestController = new AbortController()
        const requestTimeout = setTimeout(() => requestController.abort(), CONNECT_TIMEOUT_MS)
        let response
        try {
          response = await fetch(this.playUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/sdp' },
            body: peer.localDescription.sdp,
            signal: requestController.signal
          })
        } catch (error) {
          if (error && error.name === 'AbortError') throw new Error('WHEP 请求超时')
          throw error
        } finally {
          clearTimeout(requestTimeout)
        }
        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(`WHEP 请求失败：HTTP ${response.status} ${errorText}`)
        }

        const locationHeader = response.headers.get('Location')
        const createdResource = locationHeader ? new URL(locationHeader, this.playUrl).href : null
        let answer = await response.text()
        const payloadMatch = peer.localDescription.sdp.match(/a=rtpmap:(\d+) H265\/90000/i)
        const payload = payloadMatch ? payloadMatch[1] : null
        if (payload) {
          const offeredFmtpMatch = peer.localDescription.sdp.match(new RegExp('^a=fmtp:' + payload + ' .+$', 'mi'))
          const offeredFmtp = offeredFmtpMatch ? offeredFmtpMatch[0] : null
          answer = answer.replace(
            new RegExp('^a=rtpmap:' + payload + ' H264/90000$', 'mi'),
            'a=rtpmap:' + payload + ' H265/90000'
          )
          if (offeredFmtp) {
            answer = answer.replace(new RegExp('^a=fmtp:' + payload + ' .+$', 'mi'), offeredFmtp)
          }
        }

        if (requestId !== this.playRequestId || this.peerConnection !== peer) {
          this.releaseWhepResource(createdResource)
          peer.close()
          return
        }
        this.whepResource = createdResource
        await peer.setRemoteDescription({ type: 'answer', sdp: answer })
        console.log('H.265 WHEP 播放连接成功')
        this.scheduleVideoFrameCheck()
      } catch (e) {
        if (requestId !== this.playRequestId) return
        console.error('播放失败', e)
        this.videoStatus = 'error'
        this.closeVideoSession()
        this.scheduleReconnect(e instanceof Error ? e.message : '视频连接失败')
      }
    },
    waitForIceGathering (peer) {
      if (peer.iceGatheringState === 'complete') return Promise.resolve()
      return new Promise((resolve, reject) => {
        const finish = callback => {
          clearTimeout(timer)
          peer.removeEventListener('icegatheringstatechange', handleChange)
          callback()
        }
        const handleChange = () => {
          if (peer.iceGatheringState !== 'complete') return
          finish(resolve)
        }
        const timer = setTimeout(() => finish(() => reject(new Error('ICE 候选收集超时'))), CONNECT_TIMEOUT_MS)
        peer.addEventListener('icegatheringstatechange', handleChange)
      })
    },
    closeVideoSession () {
      this.clearConnectionTimer()
      this.clearStatsTimer()
      if (this.peerConnection) {
        this.peerConnection.ontrack = null
        this.peerConnection.onconnectionstatechange = null
        try { this.peerConnection.close() } catch (error) { console.warn('关闭视频连接失败', error) }
        this.peerConnection = null
      }
      const resource = this.whepResource
      this.whepResource = null
      this.releaseWhepResource(resource)
      if (this.$refs.video) this.$refs.video.srcObject = null
      if (this.$refs.videoBg) this.$refs.videoBg.srcObject = null
    },
    releaseWhepResource (resource) {
      if (!resource) return
      fetch(resource, { method: 'DELETE', keepalive: true }).catch(error => {
        console.warn('关闭 WHEP 会话失败', error)
      })
    },
    refreshVideo () {
      this.videoSuspended = false
      this.manualStop = false
      this.reconnectAttempt = 0
      this.clearReconnectTimer()
      this.startPlay(false)
    },
    suspendVideo () {
      if (this.videoSuspended) return
      this.videoSuspended = true
      this.manualStop = true
      this.playRequestId += 1
      this.clearReconnectTimer()
      this.clearVideoCheckTimer()
      this.clearStallCheckTimer()
      this.closeVideoSession()
    },
    resumeVideo () {
      if (!this.videoSuspended || document.hidden) return
      this.videoSuspended = false
      this.manualStop = false
      this.startPlay(false)
    },
    scheduleReconnect (reason, delayOverride = null) {
      if (this.manualStop || this.reconnectTimer) return
      const delay = delayOverride === null
        ? (FAST_RECONNECT_DELAYS_MS[this.reconnectAttempt] ?? SLOW_RECONNECT_DELAY_MS)
        : delayOverride
      this.reconnectAttempt += 1
      this.videoStatus = 'reconnecting'
      this.isVideoStalled = true
      console.warn(`[VideoBox] ${reason}，${delay}ms 后重连`)
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null
        this.startPlay(true)
      }, delay)
    },
    clearReconnectTimer () {
      if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    },
    clearConnectionTimer () {
      if (this.connectionTimer) clearTimeout(this.connectionTimer)
      this.connectionTimer = null
    },
    clearStatsTimer () {
      if (this.statsTimer) clearInterval(this.statsTimer)
      this.statsTimer = null
    },
    startWebRtcStatsCheck (peer, requestId) {
      this.clearStatsTimer()
      const connectedAt = Date.now()
      let lastDecodedFrames = 0
      let lastFrameAt = connectedAt
      this.statsTimer = setInterval(async () => {
        if (requestId !== this.playRequestId || this.peerConnection !== peer) return
        let stats
        try {
          stats = await peer.getStats()
        } catch (error) {
          if (requestId === this.playRequestId && this.peerConnection === peer) {
            this.scheduleReconnect('读取 WebRTC 播放状态失败')
          }
          return
        }
        if (requestId !== this.playRequestId || this.peerConnection !== peer) return
        const inbound = [...stats.values()].find(report =>
          report.type === 'inbound-rtp' && (report.kind === 'video' || report.mediaType === 'video')
        )
        const now = Date.now()
        if (!inbound) {
          if (now - connectedAt >= FIRST_FRAME_TIMEOUT_MS) this.scheduleReconnect('连接成功但未收到视频')
          return
        }
        const frames = Number(inbound.framesDecoded) || 0
        if (frames > lastDecodedFrames) {
          lastDecodedFrames = frames
          lastFrameAt = now
          this.reconnectAttempt = 0
        } else if (frames === 0 && now - connectedAt >= FIRST_FRAME_TIMEOUT_MS) {
          this.scheduleReconnect('连接成功但未解码首帧')
        } else if (frames > 0 && now - lastFrameAt >= MEDIA_STALL_TIMEOUT_MS) {
          this.scheduleReconnect('视频解码已停滞')
        }
      }, STATS_INTERVAL_MS)
    },
    clearVideoCheckTimer () {
      if (this.videoCheckTimer) {
        clearTimeout(this.videoCheckTimer)
        this.videoCheckTimer = null
      }
    },
    clearStallCheckTimer () {
      if (this.stallCheckTimer) {
        clearInterval(this.stallCheckTimer)
        this.stallCheckTimer = null
      }
    },
    handleVideoReady () {
      if (this.reconnectTimer) return
      this.videoStatus = 'ready'
      this.isVideoStalled = false
      this.clearVideoCheckTimer()
      this.startStallCheck()
    },
    startStallCheck () {
      this.clearStallCheckTimer()
      const video = this.$refs.video
      this.lastVideoTime = video ? video.currentTime : 0
      this.stallCount = 0

      this.stallCheckTimer = setInterval(() => {
        const currentVideo = this.$refs.video
        if (!currentVideo || this.videoStatus !== 'ready') return

        const currentTime = currentVideo.currentTime || 0
        const hasPlayableFrame = currentVideo.readyState >= 2 || currentVideo.videoWidth > 0 || currentVideo.videoHeight > 0

        if (!hasPlayableFrame || currentVideo.paused || currentVideo.ended) {
          this.isVideoStalled = false
          this.lastVideoTime = currentTime
          this.stallCount = 0
          return
        }

        if (Math.abs(currentTime - this.lastVideoTime) < 0.05) {
          this.stallCount += 1
        } else {
          this.stallCount = 0
          this.isVideoStalled = false
        }

        this.lastVideoTime = currentTime

        if (this.stallCount >= 3) {
          this.isVideoStalled = true
        }
      }, 1000)
    },
    scheduleVideoFrameCheck () {
      this.videoCheckCount = 0
      this.checkVideoFrameLater()
    },
    checkVideoFrameLater () {
      this.videoCheckTimer = setTimeout(() => {
        const video = this.$refs.video
        if (!video) return

        if (video.readyState >= 2 || video.videoWidth > 0 || video.videoHeight > 0) {
          this.handleVideoReady()
        } else {
          this.videoCheckCount += 1
          if (this.videoCheckCount >= 6) {
            this.videoStatus = 'noFrame'
            this.videoCheckTimer = null
          } else {
            this.checkVideoFrameLater()
          }
        }
      }, 1000)
    },
    getGuidedFov () {
      const fov = Number(this.guidedFov)
      return Number.isFinite(fov) && fov > 0 ? fov : 55
    },
    toggleGuided () {
      this.enableGuided = !this.enableGuided
    },
    // 视频点击事件处理：将点击位置转换为经纬度
    handleVideoClick (event) {
      /***********************
       * 1️⃣ 基础参数
       ***********************/
      const heightMeter = this.effectiveLaserHeight // 距离地面高度（米，激光测距仪）
      const fov = this.getGuidedFov() // 水平视场角读取当前飞机的数据库配置

      if (!this.isDescentActive) {
        return
      }

      if (!this.position || this.position.length === 0) {
        console.log('无飞机定位数据')
        return
      }

      const lastPosition = this.position[this.position.length - 1]
      const centerLng = lastPosition[0]
      const centerLat = lastPosition[1]
      const centerAlt = Number(lastPosition[2])

      const heading = -(this.heading) || 0

      /***********************
       * 2️⃣ 容器尺寸
       ***********************/
      const rect = this.$el.getBoundingClientRect()

      const containerWidth = rect.width
      const containerHeight = rect.height

      const clickX = event.clientX - rect.left
      const clickY = event.clientY - rect.top

      /***********************
       * 3️⃣ 计算真实视频显示区域（contain模式）
       ***********************/
      const videoRatio = 16 / 9
      const containerRatio = containerWidth / containerHeight

      let videoWidth
      let videoHeight
      let offsetX = 0
      let offsetY = 0

      if (containerRatio > videoRatio) {
        // 容器更宽 → 左右黑边
        videoHeight = containerHeight
        videoWidth = containerHeight * videoRatio
        offsetX = (containerWidth - videoWidth) / 2
      } else {
        // 容器更高 → 上下黑边
        videoWidth = containerWidth
        videoHeight = containerWidth / videoRatio
        offsetY = (containerHeight - videoHeight) / 2
      }

      /***********************
       * 4️⃣ 判断是否点击在视频内
       ***********************/
      if (
        clickX < offsetX ||
        clickX > offsetX + videoWidth ||
        clickY < offsetY ||
        clickY > offsetY + videoHeight
      ) {
        console.log('点击在黑边区域，忽略')
        return
      }

      /***********************
       * 5️⃣ 转为视频内坐标
       ***********************/
      const videoX = clickX - offsetX
      const videoY = clickY - offsetY

      const centerX = videoWidth / 2
      const centerY = videoHeight / 2

      const dxPixel = videoX - centerX
      const dyPixel = videoY - centerY

      /***********************
       * 6️⃣ 像素 → 地面米
       ***********************/
      const groundWidth =
        2 * heightMeter *
        Math.tan((fov / 2) * Math.PI / 180)

      const meterPerPixel = groundWidth / videoWidth

      const offsetMeterX = dxPixel * meterPerPixel
      let offsetMeterY = dyPixel * meterPerPixel

      offsetMeterY = -offsetMeterY // 屏幕Y向下 → 北为正

      /***********************
       * 7️⃣ 旋转航向
       ***********************/
      const headingRad = heading * Math.PI / 180

      const rotatedX =
        offsetMeterX * Math.cos(headingRad) -
        offsetMeterY * Math.sin(headingRad)

      const rotatedY =
        offsetMeterX * Math.sin(headingRad) +
        offsetMeterY * Math.cos(headingRad)

      /***********************
      * 8️⃣ 日志打印 → 经纬度 & 偏东偏北信息
      ***********************/
      if (this.enableGuided && this.flyToSinglePlane) {
        const deltaLat = rotatedY / 111320
        const deltaLng = rotatedX / (111320 * Math.cos(centerLat * Math.PI / 180))

        const newLat = centerLat + deltaLat
        const newLng = centerLng + deltaLng

        const finalLat = Number(newLat.toFixed(8)) // 厘米级
        const finalLng = Number(newLng.toFixed(8))

        // 根据符号转换方向文字
        const eastWest = rotatedX >= 0 ? `偏东: ${rotatedX.toFixed(2)} 米` : `偏西: ${Math.abs(rotatedX).toFixed(2)} 米`
        const northSouth = rotatedY >= 0 ? `偏北: ${rotatedY.toFixed(2)} 米` : `偏南: ${Math.abs(rotatedY).toFixed(2)} 米`

        console.log('点击经纬度:', finalLat, finalLng, centerAlt, 'FOV:', fov)
        console.log(`${eastWest}, ${northSouth}`)

        // 可选：语音播报
        speakText(`${eastWest}, ${northSouth}`)

        /***********************
       * 9 → 发送点飞指令
       ***********************/

        this.flyToSinglePlane(finalLng, finalLat, centerAlt, {
          speed: 0.2,
          fromLon: centerLng,
          fromLat: centerLat
        })
      }
    }
  },
  watch: {
    playUrl () {
      this.enableGuided = false
      this.startPlay()
    },
    isDescentActive (active) {
      if (!active) {
        this.enableGuided = false
      }
    },
    enableGuided (active) {
      if (active && !this.isDescentActive) {
        this.enableGuided = false
        return
      }

      this.$emit('guided-change', active)
      if (!active) return

      if (typeof this.publishFun === 'function') {
        this.publishFun('{loiterMode:1}')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/theme.scss";

.video-container {
  width: 100%;
  height: 100%;
  background: var(--ui-color-black); // 黑边
  position: relative;
  display: flex;
  justify-content: center; // 水平居中
  align-items: center; // 垂直居中
  overflow: hidden;

  .video-player {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 100%;
    object-fit: contain; // 保持比例
    filter: drop-shadow(0 0 12px rgba(var(--ui-color-black-rgb), .34));
  }

  .video-bg {
    position: absolute;
    inset: -42px;
    z-index: 1;
    width: calc(100% + 84px);
    height: calc(100% + 84px);
    object-fit: cover;
    filter: blur(32px) saturate(1.1);
    opacity: .96;
    transform: scale(1.12);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    background:
      radial-gradient(circle at center, rgba(var(--ui-color-black-rgb), 0) 52%, rgba(var(--ui-color-black-rgb), .04) 82%, rgba(var(--ui-color-black-rgb), .10) 100%);
    pointer-events: none;
  }

  .video-player::-webkit-media-controls-start-playback-button,
  .video-player::-webkit-media-controls-play-button,
  .video-player::-webkit-media-controls-overlay-play-button {
    display: none !important;
    -webkit-appearance: none;
    opacity: 0;
  }

  .video-mask {
    position: absolute;
    inset: 0;
    z-index: 80;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: var(--el-color-white);
    font-size: 16px;
    font-weight: 500;
    line-height: 1;
    text-align: center;
    background: var(--ui-color-black);
    pointer-events: none;

    .iconfont {
      font-size: 34px;
      color: rgba(var(--ui-color-white-rgb), 0.86);
    }
  }

  .video-stall-tip {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 82;
    transform: translate(-50%, -50%);
    padding: var(--ui-space-xs) var(--ui-space-sm);
    border-radius: 4px;
    color: var(--el-color-white);
    background: rgba(var(--ui-color-black-rgb), 0.58);
    font-size: 14px;
    line-height: 1;
    pointer-events: none;
  }

  .video-crosshair {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 81;
    width: 36px;
    height: 36px;
    transform: translate(-50%, -50%);
    pointer-events: none;

    &__line,
    &__center {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      background: rgba(var(--ui-color-white-rgb), 0.92);
      box-shadow: 0 0 3px rgba(var(--ui-color-black-rgb), 0.85);
    }

    &__line--x {
      width: 36px;
      height: 1px;
    }

    &__line--y {
      width: 1px;
      height: 36px;
    }

    &__center {
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: rgba(var(--ui-color-white-rgb), 0.98);
    }
  }

}
</style>
